import React, { useEffect, useState, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { fetchAuthSession } from 'aws-amplify/auth';
import axios from 'axios';
import sharcLogo from './sharc_nav_logo.png';
import ArtistList from './pages/ArtistList';
import ArtistDetail from './pages/ArtistDetail';
import VenueList from './pages/VenueList';
import VenueDetail from './pages/VenueDetail';
import AddArtist from './pages/AddArtist';
import AddVenue from './pages/AddVenue';
import AddEvent from './pages/AddEvent';
import EventList from './pages/EventList';
import EventDetail from './pages/EventDetail';
import EditArtist from './pages/EditArtist';
import EditVenue from './pages/EditVenue';
import EditEvent from './pages/EditEvent';
import SharcIdPage from './pages/SharcIdPage';
import MyProfile from './pages/MyProfile';
import MyShows from './pages/MyShows';
import PublicProfile from './pages/PublicProfile';
import MyImages from './pages/MyImages';
import ProductionRoleMapping from './pages/ProductionRoleMapping';
import MemberList from './pages/MemberList';
import MemberDetail from './pages/MemberDetail';
import AddMember from './pages/AddMember';
import EditMember from './pages/EditMember';
import FestivalList from './pages/FestivalList';
import FestivalDetail from './pages/FestivalDetail';
import AddFestival from './pages/AddFestival';
import EditFestival from './pages/EditFestival';
import PromoterList from './pages/PromoterList';
import PromoterDetail from './pages/PromoterDetail';
import AddPromoter from './pages/AddPromoter';
import EditPromoter from './pages/EditPromoter';
import ProjectList from './pages/ProjectList';
import ProjectDetail from './pages/ProjectDetail';
import AddProject from './pages/AddProject';
import EditProject from './pages/EditProject';
import Home from './pages/Home';
import HowItWorks from './pages/HowItWorks';
import MyShowsTutorial from './pages/MyShowsTutorial';
import AdminPortal from './pages/AdminPortal';
import AdminPicklists from './pages/AdminPicklists';
import AdminBulkEditArtists from './pages/AdminBulkEditArtists';
import AdminManageSharcIds from './pages/AdminManageSharcIds';
import ChangeLog from './pages/ChangeLog';
import DeletionQueue from './pages/DeletionQueue';
import { isAdmin } from './attendedUtils';
import Feed from './pages/Feed';
import Footer from './pages/Footer';
import TermsModal from './pages/TermsModal';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import config from './config';
import { useIsMobile } from './hooks/useMediaQuery';
import './App.css';

// ── Object config (alphabetical) ─────────────────────────────────────────────
const OBJECTS = [
    {
        key: 'artists',
        label: 'Artists',
        addLabel: 'Artist',
        endpoint: '/artists',
        listPath: '/artists',
        addPath: '/add-artist',
        getLabel: r => r.name,
        getSublabel: r => [r.city, r.state].filter(Boolean).join(', '),
        getPath: r => `/artists/${r.slug || r.id}`,
    },
    {
        key: 'events',
        label: 'Events',
        addLabel: 'Event',
        endpoint: '/events',
        listPath: '/events',
        addPath: '/add-event',
        getLabel: r => {
            if (!r.event_date) return r.headliner || '(no headliner)';
            const d = new Date(r.event_date).toLocaleDateString('en-US', {
                year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
            });
            return r.headliner ? `${r.headliner} — ${d}` : d;
        },
        getSublabel: r => [r.venue_name, r.venue_city].filter(Boolean).join(' · '),
        getPath: r => `/events/${r.slug || r.id}`,
    },
    {
        key: 'festivals',
        label: 'Festivals',
        addLabel: 'Festival',
        endpoint: '/festivals',
        listPath: '/festivals',
        addPath: '/add-festival',
        getLabel: r => r.name + (r.is_parent ? ' (Series)' : ''),
        getSublabel: () => '',
        getPath: r => `/festivals/${r.slug || r.id}`,
        extractData: d => d.festivals || d,
    },
    {
        key: 'promoters',
        label: 'Promoters',
        addLabel: 'Promoter',
        endpoint: '/promoters',
        listPath: '/promoters',
        addPath: '/add-promoter',
        getLabel: r => r.name,
        getSublabel: r => [r.city, r.state].filter(Boolean).join(', '),
        getPath: r => `/promoters/${r.slug || r.id}`,
        extractData: d => d.promoters || d,
    },
    {
        key: 'members',
        label: 'Members',
        addLabel: 'Member',
        endpoint: '/members',
        listPath: '/members',
        addPath: '/add-member',
        getLabel: r => r.name,
        getSublabel: r => [r.hometown_city, r.hometown_state].filter(Boolean).join(', '),
        getPath: r => `/members/${r.slug || r.id}`,
        extractData: d => d.members || d,
    },
    {
        key: 'projects',
        label: 'Projects',
        addLabel: 'Project',
        endpoint: '/projects',
        listPath: '/projects',
        addPath: '/add-project',
        getLabel: r => r.name,
        getSublabel: r => r.entity_name || '',
        getPath: r => `/projects/${r.id}`,
        extractData: d => Array.isArray(d) ? d : [],
    },
    {
        key: 'venues',
        label: 'Venues',
        addLabel: 'Venue',
        endpoint: '/venues',
        listPath: '/venues',
        addPath: '/add-venue',
        getLabel: r => r.name,
        getSublabel: r => [r.city, r.state].filter(Boolean).join(', '),
        getPath: r => `/venues/${r.slug || r.id}`,
    },
];

// ── Scroll to top on every route change ──────────────────────────────────────
function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
    return null;
}

// ── Global Search ─────────────────────────────────────────────────────────────
// Strip diacritics and punctuation so "Motorhead" matches "Motörhead",
// "carissas" matches "Carissa's Wierd", "REM" matches "R.E.M.", etc.
function normalizeStr(str) {
    return (str || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/['\u2018\u2019\u201b`\-.,:!?&]/g, '')
        .toLowerCase();
}

function GlobalSearch() {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [selectedObjKey, setSelectedObjKey] = useState('artists');
    const [query, setQuery] = useState('');
    const [records, setRecords] = useState([]);
    const [loadedKey, setLoadedKey] = useState(null);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    const obj = OBJECTS.find(o => o.key === selectedObjKey);

    const loadRecords = useCallback(async (key) => {
        if (loadedKey === key) return;
        const o = OBJECTS.find(x => x.key === key);
        if (!o) return;
        setLoading(true);
        try {
            const res = await axios.get(`${config.API_URL}${o.endpoint}`);
            const data = o.extractData ? o.extractData(res.data) : res.data;
            setRecords(Array.isArray(data) ? data : []);
            setLoadedKey(key);
        } catch (e) {
            setRecords([]);
        }
        setLoading(false);
    }, [loadedKey]);

    const handleObjChange = (e) => {
        const key = e.target.value;
        setSelectedObjKey(key);
        setQuery('');
        setOpen(false);
        setLoadedKey(null);
        setRecords([]);
    };

    const handleFocus = () => {
        loadRecords(selectedObjKey);
        setOpen(true);
    };

    const handleInputChange = (e) => {
        setQuery(e.target.value);
        setOpen(true);
    };

    useEffect(() => {
        function onClickOutside(e) {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    const q = normalizeStr(query.trim());
    const filtered = q
        ? records.filter(r =>
            normalizeStr(obj.getLabel(r)).includes(q) ||
            normalizeStr(obj.getSublabel(r)).includes(q)
          )
        : [];

    const handleSelect = (r) => {
        navigate(obj.getPath(r));
        setQuery('');
        setOpen(false);
        if (inputRef.current) inputRef.current.blur();
    };

    const showDropdown = open && q.length > 0 && (loading || filtered.length > 0 || !loading);

    return (
        <div ref={containerRef} style={isMobile ? { ...N.searchOuter, width: '100%' } : N.searchOuter}>

            {/* Object type selector */}
            <div style={N.objSelectWrap}>
                <select
                    value={selectedObjKey}
                    onChange={handleObjChange}
                    style={N.objSelect}
                >
                    {OBJECTS.map(o => (
                        <option key={o.key} value={o.key}>{o.label}</option>
                    ))}
                </select>
                <span style={N.objChevron}>▾</span>
            </div>

            {/* Text search input */}
            <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    placeholder={`Search ${obj.label.toLowerCase()}...`}
                    style={{
                        ...N.searchInput,
                        borderRadius: showDropdown ? '0 4px 0 0' : '0 4px 4px 0',
                    }}
                    autoComplete="off"
                />

                {showDropdown && (
                    <div style={N.searchDropdown}>
                        {loading && (
                            <div style={N.searchMeta}>Loading {obj.label.toLowerCase()}…</div>
                        )}
                        {!loading && filtered.length === 0 && q && (
                            <div style={N.searchMeta}>No results for "{query}"</div>
                        )}
                        {!loading && filtered.map((r, i) => (
                            <div
                                key={r.id || i}
                                onMouseDown={() => handleSelect(r)}
                                style={N.searchRow}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#383838'}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                                <span style={N.searchRowLabel}>{obj.getLabel(r)}</span>
                                {obj.getSublabel(r) && (
                                    <span style={N.searchRowSub}>{obj.getSublabel(r)}</span>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Browse Dropdown ───────────────────────────────────────────────────────────
// `inline` renders the sub-links in normal document flow (used inside the
// mobile hamburger panel) instead of an absolutely-positioned overlay, so
// they can't be clipped by the panel's own scroll container.
function BrowseDropdown({ inline = false }) {
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => { setOpen(false); }, [location.pathname]);

    useEffect(() => {
        if (inline) return; // no outside-click auto-close needed for the inline/mobile variant
        function onOut(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
        document.addEventListener('mousedown', onOut);
        return () => document.removeEventListener('mousedown', onOut);
    }, [inline]);

    const isActive = OBJECTS.some(o =>
        location.pathname === o.listPath || location.pathname.startsWith(o.listPath + '/')
    );

    if (inline) {
        return (
            <div style={N.mobileGroup}>
                <button
                    onClick={() => setOpen(v => !v)}
                    style={{
                        ...N.mobileRow,
                        color: isActive ? '#bb86fc' : '#e0e0e0',
                    }}
                >
                    <span>Browse</span>
                    <span style={N.chevron}>{open ? '▴' : '▾'}</span>
                </button>
                {open && (
                    <div style={N.mobileSubList}>
                        {OBJECTS.map(o => {
                            const active = location.pathname === o.listPath;
                            return (
                                <Link
                                    key={o.key}
                                    to={o.listPath}
                                    style={{
                                        ...N.mobileSubRow,
                                        color: active ? '#bb86fc' : '#e0e0e0',
                                    }}
                                >
                                    {o.label}
                                </Link>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button
                onClick={() => setOpen(v => !v)}
                style={{
                    ...N.ghostBtn,
                    color: isActive ? '#bb86fc' : '#ccc',
                    backgroundColor: open ? '#2c2c2c' : 'transparent',
                    borderColor: open ? '#555' : '#444',
                }}
            >
                Browse <span style={N.chevron}>{open ? '▴' : '▾'}</span>
            </button>
            {open && (
                <div style={N.menu}>
                    {OBJECTS.map(o => {
                        const active = location.pathname === o.listPath;
                        return (
                            <Link
                                key={o.key}
                                to={o.listPath}
                                style={{
                                    ...N.menuItem,
                                    color: active ? '#bb86fc' : '#e0e0e0',
                                    backgroundColor: active ? '#2a2040' : 'transparent',
                                }}
                                onMouseEnter={e => { if (!active) e.currentTarget.style.backgroundColor = '#2a2a2a'; }}
                                onMouseLeave={e => { e.currentTarget.style.backgroundColor = active ? '#2a2040' : 'transparent'; }}
                            >
                                {o.label}
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ── Add Dropdown ──────────────────────────────────────────────────────────────
// `compact` renders an icon-only "+" trigger (used as the persistent mobile
// top-bar button per the mobile spec's nav recommendation) instead of the
// "+ Add" text button. The dropdown menu itself is unchanged in either mode —
// it's already absolutely positioned against its own trigger and isn't
// nested inside any scrollable container, so it isn't at risk of clipping.
function AddDropdown({ compact = false }) {
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => { setOpen(false); }, [location.pathname]);

    useEffect(() => {
        function onOut(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
        document.addEventListener('mousedown', onOut);
        return () => document.removeEventListener('mousedown', onOut);
    }, []);

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button
                onClick={() => setOpen(v => !v)}
                aria-label="Add"
                style={{
                    ...(compact ? N.addBtnCompact : N.addBtn),
                    backgroundColor: open ? '#a370e3' : '#bb86fc',
                }}
            >
                {compact ? '+' : (<>+ Add <span style={N.chevron}>{open ? '▴' : '▾'}</span></>)}
            </button>
            {open && (
                <div style={{ ...N.menu, right: 0, left: 'auto', minWidth: '160px' }}>
                    {OBJECTS.map(o => (
                        <Link
                            key={o.key}
                            to={o.addPath}
                            style={N.addMenuItem}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#a370e3'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#bb86fc'}
                        >
                            {o.addLabel}
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Profile Dropdown ──────────────────────────────────────────────────────────
// `inline` renders the menu contents directly in normal document flow (no
// button trigger, no absolute-positioned overlay) — used inside the mobile
// hamburger panel, which is already an expand/collapse surface so a second
// nested dropdown-within-a-dropdown isn't needed and would risk clipping.
function ProfileDropdown({ signOut, user, inline = false }) {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [displayName, setDisplayName] = useState(null);
    const [isAdminUser, setIsAdminUser] = useState(false);
    const ref = useRef(null);

    // Load display name from API on mount
    useEffect(() => {
        async function loadUser() {
            try {
                const { tokens } = await fetchAuthSession();
                const idToken = tokens.idToken.toString();
                const res = await axios.get(`${config.API_URL}/user`, {
                    headers: { Authorization: idToken }
                });
                setDisplayName(res.data.display_name || null);
            } catch (e) {
                // silently fail — initials fallback will show
            }
        }
        loadUser();
    }, []);

    // Check admin group membership
    useEffect(() => {
        isAdmin().then(result => setIsAdminUser(!!result)).catch(() => setIsAdminUser(false));
    }, []);

    // Close on outside click
    useEffect(() => {
        function onOut(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
        document.addEventListener('mousedown', onOut);
        return () => document.removeEventListener('mousedown', onOut);
    }, []);

    // Build initials for avatar
    const email = user?.signInDetails?.loginId || user?.username || '';
    const initials = displayName
        ? displayName.trim().split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2)
        : email ? email[0].toUpperCase()
        : '?';

    const handleMyProfile  = () => { setOpen(false); navigate('/profile'); };
    const handleMyShows    = () => { setOpen(false); navigate('/my-shows'); };
    const handleMyImages   = () => { setOpen(false); navigate('/my-images'); };
    const handleAdminPortal = () => { setOpen(false); navigate('/admin'); };
    const handleSignOut    = () => { setOpen(false); signOut(); };

    if (inline) {
        return (
            <div style={N.mobileGroup}>
                <div style={N.profileMenuHeader}>
                    <div style={N.profileMenuName}>{displayName || 'My Account'}</div>
                    {displayName && <div style={N.profileMenuEmail}>{email}</div>}
                </div>
                <div style={N.profileMenuDivider} />
                <button style={N.mobileRow} onClick={handleMyProfile}>My Profile</button>
                <button style={N.mobileRow} onClick={handleMyShows}>My Shows</button>
                <button style={N.mobileRow} onClick={handleMyImages}>My Images</button>
                {isAdminUser && (
                    <button style={N.mobileRow} onClick={handleAdminPortal}>Admin Portal</button>
                )}
                <div style={N.profileMenuDivider} />
                <button style={{ ...N.mobileRow, color: '#e57373' }} onClick={handleSignOut}>Sign Out</button>
            </div>
        );
    }

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button
                onClick={() => setOpen(v => !v)}
                style={{
                    ...N.profileBtn,
                    borderColor: open ? '#bb86fc' : '#555',
                    backgroundColor: open ? '#2a2040' : '#2c2c2c',
                }}
                title={displayName || email}
            >
                <span style={N.profileInitials}>{initials}</span>
                <span style={N.chevron}>{open ? '▴' : '▾'}</span>
            </button>

            {open && (
                <div style={{ ...N.menu, right: 0, left: 'auto', minWidth: '180px' }}>
                    {/* Name / email header */}
                    <div style={N.profileMenuHeader}>
                        <div style={N.profileMenuName}>{displayName || 'My Account'}</div>
                        {displayName && <div style={N.profileMenuEmail}>{email}</div>}
                    </div>
                    <div style={N.profileMenuDivider} />
                    <button style={N.profileMenuItem} onClick={handleMyProfile}>My Profile</button>
                    <button style={N.profileMenuItem} onClick={handleMyShows}>My Shows</button>
                    <button style={N.profileMenuItem} onClick={handleMyImages}>My Images</button>
                    {isAdminUser && (
                        <button style={N.profileMenuItem} onClick={handleAdminPortal}>Admin Portal</button>
                    )}
                    <div style={N.profileMenuDivider} />
                    <button style={{ ...N.profileMenuItem, color: '#e57373' }} onClick={handleSignOut}>Sign Out</button>
                </div>
            )}
        </div>
    );
}

// ── NavBar ────────────────────────────────────────────────────────────────────
function NavBar({ signOut, user }) {
    const location = useLocation();
    const isMobile = useIsMobile();
    const [mobileOpen, setMobileOpen] = useState(false);

    // Close the mobile panel on every route change
    useEffect(() => { setMobileOpen(false); }, [location.pathname]);

    // Below 768px: collapse Feed / Projects / Browse / profile into a
    // hamburger panel; keep + Add as a persistent icon-only button next to
    // the hamburger (per SHARC_Mobile_Responsive_Spec_v1.1, Section 5.1 /
    // Decisions Log). Desktop layout (>=768px) is unchanged.
    if (isMobile) {
        return (
            <>
                <nav style={N.nav}>
                    <Link to="/" style={N.brandLink}>
                        <img src={sharcLogo} alt="SHARC" style={N.logo} />
                    </Link>
                    <div style={{ flex: 1 }} />
                    <div style={N.rightGroup}>
                        <AddDropdown compact />
                        <button
                            onClick={() => setMobileOpen(v => !v)}
                            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
                            aria-expanded={mobileOpen}
                            style={{
                                ...N.hamburgerBtn,
                                borderColor: mobileOpen ? '#bb86fc' : '#444',
                                color: mobileOpen ? '#bb86fc' : '#ccc',
                            }}
                        >
                            {mobileOpen ? '✕' : '☰'}
                        </button>
                    </div>
                </nav>

                {mobileOpen && (
                    <div style={N.mobilePanel}>
                        <div style={N.mobileGroup}>
                            <GlobalSearch />
                        </div>
                        <div style={N.mobileDivider} />

                        <Link
                            to="/feed"
                            style={{
                                ...N.mobileRow,
                                color: location.pathname.startsWith('/feed') ? '#bb86fc' : '#e0e0e0',
                                textDecoration: 'none',
                            }}
                        >
                            Feed
                        </Link>
                        <Link
                            to="/projects"
                            style={{
                                ...N.mobileRow,
                                color: location.pathname.startsWith('/projects') ? '#bb86fc' : '#e0e0e0',
                                textDecoration: 'none',
                            }}
                        >
                            Projects
                        </Link>
                        <BrowseDropdown inline />

                        <div style={N.mobileDivider} />
                        <ProfileDropdown signOut={signOut} user={user} inline />
                    </div>
                )}
            </>
        );
    }

    return (
        <nav style={N.nav}>
            {/* Logo — left edge */}
            <Link to="/" style={N.brandLink}>
                <img src={sharcLogo} alt="SHARC" style={N.logo} />
            </Link>

            {/* Search — fixed width, right next to logo */}
            <GlobalSearch />

            {/* Flexible spacer pushes everything else to the right */}
            <div style={{ flex: 1 }} />

            {/* Right-side controls */}
            <div style={N.rightGroup}>
                <Link
                    to="/feed"
                    style={{
                        ...N.ghostBtn,
                        color: location.pathname.startsWith('/feed') ? '#bb86fc' : '#ccc',
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                    }}
                >
                    Feed
                </Link>
                <Link
                    to="/projects"
                    style={{
                        ...N.ghostBtn,
                        color: location.pathname.startsWith('/projects') ? '#bb86fc' : '#ccc',
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                    }}
                >
                    Projects
                </Link>
                <BrowseDropdown />
                <AddDropdown />
                <div style={N.divider} />
                <ProfileDropdown signOut={signOut} user={user} />
            </div>
        </nav>
    );
}

// ── Terms Gate ────────────────────────────────────────────────────────────────
// Checks if the logged-in user has accepted terms. Shows modal if not.
// Pages that are always accessible regardless of T&C acceptance status
const TERMS_EXEMPT_PATHS = ['/terms', '/privacy'];

function TermsGate({ children }) {
    const [termsStatus, setTermsStatus] = useState('loading'); // 'loading' | 'accepted' | 'pending'
    const location = useLocation();

    // Skip the gate entirely on the terms/privacy pages so users can read before accepting
    const isExempt = TERMS_EXEMPT_PATHS.includes(location.pathname);

    useEffect(() => {
        if (isExempt) return; // No need to check if we're on an exempt page
        async function checkTerms() {
            try {
                const { tokens } = await fetchAuthSession();
                const idToken = tokens.idToken.toString();
                const res = await axios.get(`${config.API_URL}/user`, {
                    headers: { Authorization: idToken }
                });
                if (res.data.terms_accepted_at) {
                    setTermsStatus('accepted');
                } else {
                    setTermsStatus('pending');
                }
            } catch (e) {
                // On error, let them through — don't block on a network hiccup
                setTermsStatus('accepted');
            }
        }
        checkTerms();
    }, [isExempt]);

    const handleAccept = async () => {
        const { tokens } = await fetchAuthSession();
        const idToken = tokens.idToken.toString();
        await axios.put(`${config.API_URL}/user`, { action: 'acceptTerms' }, {
            headers: { Authorization: idToken }
        });
        setTermsStatus('accepted');
    };

    // Always render children on exempt paths — no loading state, no modal
    if (isExempt) {
        return <>{children}</>;
    }

    if (termsStatus === 'loading') {
        return (
            <div style={{ minHeight: '100vh', backgroundColor: '#121212', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ color: '#666', fontSize: '14px' }}>Loading…</div>
            </div>
        );
    }

    return (
        <>
            {termsStatus === 'pending' && <TermsModal onAccept={handleAccept} />}
            {children}
        </>
    );
}

// ── Admin route guard ────────────────────────────────────────────────────────
// Wraps a route element and redirects non-admins back to /admin. This is a UX
// convenience only — the real enforcement is the 403 each admin Lambda already
// returns server-side (getDeletionQueue, permanentlyDeleteRequests,
// reverseDeletionRequests), since this client-side check can be bypassed.
function AdminRoute({ children }) {
    const [status, setStatus] = useState('checking'); // 'checking' | 'allowed' | 'denied'

    useEffect(() => {
        isAdmin()
            .then(result => setStatus(result ? 'allowed' : 'denied'))
            .catch(() => setStatus('denied'));
    }, []);

    if (status === 'checking') return null;
    if (status === 'denied') return <Navigate to="/" replace />;
    return children;
}

// ── App Shell ─────────────────────────────────────────────────────────────────
// Pulled out from the Authenticator render-prop into its own component so
// hooks (useIsMobile) are called from a proper function component rather
// than from inside a third-party render-prop callback.
function AppShell({ signOut, user }) {
    const isMobile = useIsMobile();
    const mainStyle = isMobile ? { ...S.main, ...S.mainMobile } : S.main;

    return (
        <Router>
            <ScrollToTop />
            <TermsGate>
                <div style={S.app}>
                    <NavBar signOut={signOut} user={user} />
                    <main style={mainStyle}>
                        <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/how-it-works" element={<HowItWorks />} />
                            <Route path="/my-shows-tutorial" element={<MyShowsTutorial />} />
                            <Route path="/artists" element={<ArtistList />} />
                            <Route path="/artists/:slug" element={<ArtistDetail />} />
                            <Route path="/venues" element={<VenueList />} />
                            <Route path="/venues/:slug" element={<VenueDetail />} />
                            <Route path="/events" element={<EventList />} />
                            <Route path="/events/:slug" element={<EventDetail />} />
                            <Route path="/artists/:slug/edit" element={<EditArtist />} />
                            <Route path="/venues/:slug/edit" element={<EditVenue />} />
                            <Route path="/events/:slug/edit" element={<EditEvent />} />
                            <Route path="/add-artist" element={<AddArtist />} />
                            <Route path="/add-venue" element={<AddVenue />} />
                            <Route path="/add-event" element={<AddEvent />} />
                            <Route path="/projects" element={<ProjectList />} />
                            <Route path="/projects/:id" element={<ProjectDetail />} />
                            <Route path="/projects/:id/edit" element={<EditProject />} />
                            <Route path="/add-project" element={<AddProject />} />
                            <Route path="/festivals" element={<FestivalList />} />
                            <Route path="/festivals/:slug" element={<FestivalDetail />} />
                            <Route path="/festivals/:slug/edit" element={<EditFestival />} />
                            <Route path="/add-festival" element={<AddFestival />} />
                            <Route path="/promoters" element={<PromoterList />} />
                            <Route path="/promoters/:slug" element={<PromoterDetail />} />
                            <Route path="/promoters/:slug/edit" element={<EditPromoter />} />
                            <Route path="/add-promoter" element={<AddPromoter />} />
                            <Route path="/members" element={<MemberList />} />
                            <Route path="/members/:slug" element={<MemberDetail />} />
                            <Route path="/members/:slug/edit" element={<EditMember />} />
                            <Route path="/add-member" element={<AddMember />} />
                            <Route path="/production-role-mapping/:memberId" element={<ProductionRoleMapping />} />
                            <Route path="/admin" element={<AdminRoute><AdminPortal /></AdminRoute>} />
                            <Route path="/admin/picklists" element={<AdminRoute><AdminPicklists /></AdminRoute>} />
                            <Route path="/admin/bulk-edit-artists" element={<AdminRoute><AdminBulkEditArtists /></AdminRoute>} />
                            <Route path="/admin/sharc-ids" element={<AdminRoute><AdminManageSharcIds /></AdminRoute>} />
                            <Route path="/admin/change-log" element={<AdminRoute><ChangeLog /></AdminRoute>} />
                            <Route path="/admin/deletion-queue" element={<AdminRoute><DeletionQueue /></AdminRoute>} />
                            <Route path="/feed" element={<Feed />} />
                            <Route path="/:sharcId" element={<SharcIdPage />} />
                            <Route path="/profile" element={<MyProfile />} />
                            <Route path="/my-shows" element={<MyShows />} />
                            <Route path="/u/:username" element={<PublicProfile />} />
                            <Route path="/my-images" element={<MyImages />} />
                            <Route path="/terms" element={<TermsPage />} />
                            <Route path="/privacy" element={<PrivacyPage />} />
                        </Routes>
                    </main>
                    <Footer />
                </div>
            </TermsGate>
        </Router>
    );
}

// ── App ───────────────────────────────────────────────────────────────────────
function App() {
    return (
        <Authenticator hideSignUp={true}>
            {({ signOut, user }) => <AppShell signOut={signOut} user={user} />}
        </Authenticator>
    );
}

// ── Nav styles (N) ────────────────────────────────────────────────────────────
const N = {
    nav: {
        backgroundColor: '#1e1e1e',
        borderBottom: '1px solid #333',
        padding: '0 20px',
        display: 'flex',
        alignItems: 'center',
        height: '60px',
        position: 'sticky',
        top: 0,
        zIndex: 200,
        gap: '12px',
    },
    logo: { height: '28px', width: 'auto', display: 'block' },
    brandLink: { display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 },

    // Search
    searchOuter: {
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        width: '380px',
    },
    objSelectWrap: {
        position: 'relative',
        flexShrink: 0,
    },
    objSelect: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRight: 'none',
        borderRadius: '4px 0 0 4px',
        color: '#e0e0e0',
        fontSize: '13px',
        padding: '8px 28px 8px 10px',
        height: '36px',
        cursor: 'pointer',
        outline: 'none',
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
    },
    objChevron: {
        position: 'absolute',
        right: '7px',
        top: '50%',
        transform: 'translateY(-50%)',
        fontSize: '11px',
        color: '#999',
        pointerEvents: 'none',
        lineHeight: 1,
    },
    searchInput: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderLeft: 'none',
        borderRadius: '0 4px 4px 0',
        color: '#e0e0e0',
        fontSize: '14px',
        padding: '8px 12px',
        width: '100%',
        height: '36px',
        boxSizing: 'border-box',
        outline: 'none',
    },
    searchDropdown: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderTop: 'none',
        borderRadius: '0 0 6px 6px',
        maxHeight: '340px',
        overflowY: 'auto',
        zIndex: 300,
        boxShadow: '0 8px 24px rgba(0,0,0,0.55)',
    },
    searchMeta: {
        padding: '10px 14px',
        fontSize: '13px',
        color: '#777',
    },
    searchRow: {
        padding: '9px 14px',
        cursor: 'pointer',
        borderBottom: '1px solid #333',
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        backgroundColor: 'transparent',
    },
    searchRowLabel: { fontSize: '14px', color: '#e0e0e0' },
    searchRowSub:   { fontSize: '12px', color: '#888' },

    // Browse / Add
    rightGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexShrink: 0,
    },
    ghostBtn: {
        background: 'transparent',
        border: '1px solid #444',
        borderRadius: '4px',
        color: '#ccc',
        fontSize: '14px',
        padding: '7px 13px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        whiteSpace: 'nowrap',
    },
    addBtn: {
        backgroundColor: '#bb86fc',
        border: 'none',
        borderRadius: '4px',
        color: '#121212',
        fontSize: '14px',
        fontWeight: 'bold',
        padding: '7px 14px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        whiteSpace: 'nowrap',
    },
    // Icon-only "+" trigger — persistent mobile top-bar button (44x44 touch target)
    addBtnCompact: {
        backgroundColor: '#bb86fc',
        border: 'none',
        borderRadius: '4px',
        color: '#121212',
        fontSize: '22px',
        fontWeight: 'bold',
        lineHeight: 1,
        width: '40px',
        height: '40px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    chevron: { fontSize: '10px', opacity: 0.75 },

    // ── Mobile hamburger nav ──
    hamburgerBtn: {
        background: 'transparent',
        border: '1px solid #444',
        borderRadius: '4px',
        fontSize: '20px',
        lineHeight: 1,
        width: '40px',
        height: '40px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    mobilePanel: {
        position: 'fixed',
        top: '60px',
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#1e1e1e',
        borderTop: '1px solid #333',
        zIndex: 199,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        padding: '8px 16px 24px',
        boxSizing: 'border-box',
    },
    mobileGroup: {
        display: 'flex',
        flexDirection: 'column',
    },
    mobileDivider: {
        height: '1px',
        backgroundColor: '#333',
        margin: '8px 0',
    },
    // Full-width, ~44px-tall tap target rows for links/buttons in the mobile panel
    mobileRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        boxSizing: 'border-box',
        background: 'none',
        border: 'none',
        padding: '13px 4px',
        fontSize: '16px',
        color: '#e0e0e0',
        cursor: 'pointer',
        textAlign: 'left',
    },
    mobileSubList: {
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: '16px',
    },
    mobileSubRow: {
        display: 'block',
        padding: '12px 4px',
        fontSize: '15px',
        textDecoration: 'none',
        color: '#e0e0e0',
    },
    menu: {
        position: 'absolute',
        top: 'calc(100% + 5px)',
        left: 0,
        backgroundColor: '#1e1e1e',
        border: '1px solid #444',
        borderRadius: '6px',
        minWidth: '160px',
        zIndex: 300,
        boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
        overflow: 'hidden',
    },
    menuItem: {
        display: 'block',
        padding: '10px 16px',
        fontSize: '14px',
        textDecoration: 'none',
        color: '#e0e0e0',
    },
    addMenuItem: {
        display: 'block',
        padding: '10px 16px',
        fontSize: '14px',
        fontWeight: '600',
        textDecoration: 'none',
        color: '#121212',
        backgroundColor: '#bb86fc',
        borderBottom: '1px solid rgba(0,0,0,0.12)',
        textAlign: 'right',
    },
    divider: {
        width: '1px',
        height: '24px',
        backgroundColor: '#444',
        margin: '0 2px',
    },
    signOutBtn: {
        backgroundColor: 'transparent',
        color: '#aaa',
        border: '1px solid #444',
        borderRadius: '4px',
        padding: '7px 12px',
        fontSize: '13px',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    // Profile dropdown
    profileBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        backgroundColor: '#2c2c2c',
        border: '1px solid #555',
        borderRadius: '6px',
        padding: '5px 10px',
        cursor: 'pointer',
        color: '#e0e0e0',
        fontSize: '13px',
        whiteSpace: 'nowrap',
        transition: 'border-color 0.15s, background-color 0.15s',
    },
    profileInitials: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '26px',
        height: '26px',
        borderRadius: '4px',
        backgroundColor: '#bb86fc',
        color: '#121212',
        fontSize: '12px',
        fontWeight: 'bold',
        letterSpacing: '0.5px',
        flexShrink: 0,
    },
    profileMenuHeader: {
        padding: '10px 16px 8px',
    },
    profileMenuName: {
        fontSize: '14px',
        fontWeight: 'bold',
        color: '#e0e0e0',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    profileMenuEmail: {
        fontSize: '11px',
        color: '#777',
        marginTop: '2px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    profileMenuDivider: {
        height: '1px',
        backgroundColor: '#333',
        margin: '2px 0',
    },
    profileMenuItem: {
        display: 'block',
        width: '100%',
        padding: '10px 16px',
        fontSize: '14px',
        color: '#e0e0e0',
        background: 'none',
        border: 'none',
        textAlign: 'left',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
};

// ── Page styles (S) ───────────────────────────────────────────────────────────
const S = {
    app: { minHeight: '100vh', backgroundColor: '#121212', display: 'flex', flexDirection: 'column' },
    main: { width: '100%', margin: '0 auto', padding: '32px 100px', flex: 1, minWidth: 0, boxSizing: 'border-box' },
    // Mobile override (Spec Section 5.1 / Decisions Log — 16px). Merged over
    // `main` on top, so only padding changes; everything else is inherited.
    mainMobile: { padding: '16px' },
};

export default App;
